import { IconButton, Stack } from '@mui/material';
import { FilterKeys } from '@schooly/api';
import { PropertyTypeTagSelect, SubjectTagSelect } from '@schooly/components/filters';
import { SchoolUserRole } from '@schooly/constants';
import { CrossIcon, TagSelect } from '@schooly/style';
import React, { FC, ReactNode, useMemo } from 'react';

import { useSchool } from '../../../hooks/useSchool';
import { AddStaffFilters, AddStaffFiltersKey } from './AddStaffFiltersPopup';

type InlineFiltersTagsProps = {
  filtersAvailable: AddStaffFiltersKey[];
  onOpenFilters: () => void;
  onResetFilters: () => void;
  maxAmount?: number;
  filters: AddStaffFilters;
};

export const InlineFiltersTags: FC<InlineFiltersTagsProps> = ({
  filtersAvailable,
  filters,
  onOpenFilters,
  onResetFilters,
  maxAmount = 2,
}) => {
  const { schoolId } = useSchool();

  const tags = useMemo(() => {
    const tagComponents: ReactNode[] = [];

    if (!schoolId) return tagComponents;

    const commonTagsProps = {
      size: 'small',
      onClick: onOpenFilters,
      sx: { maxWidth: 115 },
    } as const;

    for (const id of [
      ...(filtersAvailable.includes(FilterKeys.Status) ? filters.status || [] : []),
      ...(filtersAvailable.includes(FilterKeys.House) ? filters.house || [] : []),
    ]) {
      tagComponents.push(
        <PropertyTypeTagSelect
          key={id}
          id={id}
          schoolId={schoolId}
          userRole={SchoolUserRole.Staff}
          {...commonTagsProps}
        />,
      );
    }

    for (const id of filtersAvailable.includes(FilterKeys.Subject) ? filters.subject || [] : []) {
      tagComponents.push(
        <SubjectTagSelect key={id} id={id} schoolId={schoolId} {...commonTagsProps} />,
      );
    }

    if (tagComponents.length > maxAmount) {
      return [
        ...tagComponents.slice(0, maxAmount),
        <TagSelect
          key={`+${tagComponents.length - maxAmount}`}
          label={`+${tagComponents.length - maxAmount}`}
          {...commonTagsProps}
        />,
      ];
    }

    return tagComponents;
  }, [filtersAvailable, filters, onOpenFilters, schoolId, maxAmount]);

  if (!tags.length) return null;

  return (
    <Stack flexDirection="row" flexWrap="wrap" gap={1}>
      {tags}{' '}
      <IconButton
        onClick={onResetFilters}
        sx={(theme) => ({
          color: theme.palette.common.grey,
          '&:hover': { color: theme.palette.text.primary },
        })}
      >
        <CrossIcon />
      </IconButton>
    </Stack>
  );
};
